import React from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { formatGiftedStat } from './gifted-stat';
import { RelationshipKey } from '../home/routes/connections/relationship-nav';
import { ProfileRelationshipStats, RELATIONSHIP_STATS } from './relationship-stats';

const profileValueSx = {
  fontWeight: 700,
  color: 'var(--qc-accent, #2a9dff)',
  fontSize: { sm: 22 },
};

const profileLabelSx = {
  color: 'var(--qc-shell-muted, #a9b5ca)',
  fontSize: { sm: 16 },
};

const listValueSx = {
  fontWeight: 700,
  color: 'var(--qc-accent, #2a9dff)',
  fontSize: 16,
};

const listLabelSx = {
  color: 'var(--qc-shell-muted, #a9b5ca)',
  fontSize: 12,
  whiteSpace: 'nowrap',
};

type RelationshipStatFiguresProps = {
  stats: ProfileRelationshipStats | null;
  density: 'profile' | 'list';
  clickZero?: boolean;
  onStat: (key: RelationshipKey) => void;
  giftedAccessory?: React.ReactNode;
};

function statCount(stats: ProfileRelationshipStats, key: RelationshipKey): number | null {
  if (key === 'gifted') return typeof stats.gifted === 'number' ? stats.gifted : null;
  return stats[key];
}

function RelationshipStatFigures({
  stats,
  density,
  clickZero,
  onStat,
  giftedAccessory,
}: RelationshipStatFiguresProps): JSX.Element {
  const valueSx = density === 'profile' ? profileValueSx : listValueSx;
  const labelSx = density === 'profile' ? profileLabelSx : listLabelSx;
  return (
    <Stack
      direction="row"
      spacing={density === 'profile' ? 2 : 0.5}
      flexWrap={density === 'profile' ? 'wrap' : { xs: 'wrap', sm: 'nowrap' }}
      useFlexGap
      sx={density === 'list' ? { width: '100%', minWidth: 0 } : undefined}
    >
      {RELATIONSHIP_STATS.map(({ key, label }) => {
        const count = stats ? statCount(stats, key) : null;
        const clickable = count !== null && (Boolean(clickZero) || count > 0);
        const value = key === 'gifted' ? formatGiftedStat(count) : (count ?? '—');
        const figure = (
          <Stack alignItems="center">
            <Typography sx={valueSx}>{value}</Typography>
            <Typography variant="caption" sx={labelSx}>{label}</Typography>
          </Stack>
        );
        return (
          <Stack
            key={key}
            alignItems="center"
            direction="row"
            spacing={0.25}
            sx={density === 'profile'
              ? { minWidth: 72 }
              : { flex: { xs: '1 1 72px', sm: '1 1 0' }, minWidth: { xs: 72, sm: 0 } }}
          >
            {clickable ? (
              <Button
                sx={{
                  textDecoration: 'none',
                  flexDirection: 'column',
                  alignItems: 'center',
                  color: 'inherit',
                  p: 0,
                  minWidth: 0,
                }}
                onClick={() => onStat(key)}
              >
                {figure}
              </Button>
            ) : figure}
            {key === 'gifted' ? giftedAccessory : null}
          </Stack>
        );
      })}
    </Stack>
  );
}

RelationshipStatFigures.defaultProps = {
  clickZero: true,
  giftedAccessory: undefined,
};

export default RelationshipStatFigures;
